import { Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { usePortal } from "@/features/portal/portal-context";
import { landingPath } from "@/features/portal/navigation";
import { PopinMark } from "@/components/popin-mark";
import { BackendUnavailablePage } from "@/features/auth/backend-unavailable-page";
import { PartnersOnlyPage } from "@/features/auth/partners-only-page";

/** Shown while the initial Firebase auth state + role check is resolving. */
function AuthLoading() {
  return (
    <div className="grid min-h-screen place-items-center bg-background text-foreground">
      <div className="flex flex-col items-center gap-4">
        <PopinMark size={30} />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Checking your session…
        </div>
      </div>
    </div>
  );
}

/** Gate for the authenticated portal. Sends signed-out users to /login. */
export function RequireAuth() {
  const { authReady, authed, backendUnavailable, accessDenied } = usePortal();
  if (!authReady) return <AuthLoading />;
  if (backendUnavailable) return <BackendUnavailablePage />;
  if (accessDenied) return <PartnersOnlyPage />;
  return authed ? <Outlet /> : <Navigate to="/login" replace />;
}

/** Keeps signed-in users out of /login. */
export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { authReady, authed, backendUnavailable, accessDenied, brands, canCreateBrand, isSuperAdmin } = usePortal();
  if (!authReady) return <AuthLoading />;
  // Signed in to Firebase but the backend is down: the interstitial applies here
  // too — the user is effectively logged in, just can't load the portal yet.
  if (backendUnavailable) return <BackendUnavailablePage />;
  // Signed in but without portal access — show the partners-only page rather than
  // the login form (they're authenticated; they just can't use the portal).
  if (accessDenied) return <PartnersOnlyPage />;
  return authed ? <Navigate to={landingPath({ brands, canCreateBrand, isSuperAdmin })} replace /> : <>{children}</>;
}

/**
 * Index redirect for the authenticated portal root. Sends the user to their
 * first brand's editor, the create flow, or (super admins with no brands) the
 * cross-brand directory. Replaces the old hardcoded `/theming` default and the
 * `RequireBrand` brand-less bounce.
 */
export function LandingRedirect() {
  const { brands, canCreateBrand, isSuperAdmin } = usePortal();
  return <Navigate to={landingPath({ brands, canCreateBrand, isSuperAdmin })} replace />;
}

/**
 * Gate for admin-only pages (the "All brands" directory). Non-admins are bounced
 * to the portal root rather than shown a forbidden page — the nav entry that
 * leads here is itself hidden for them, so this only catches direct URL access.
 */
export function RequireSuperAdmin() {
  const { isSuperAdmin } = usePortal();
  if (!isSuperAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}
